import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { fetchConToken } from '../../../helpers/fetch';

function CommercialHistory({ lead }) {
    const [open, setOpen] = useState(false);
    const [history, setHistory] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!open) return;
        const controller = new AbortController();
        let active = true;
        setHistory(null); setError(null);
        (async () => {
            try {
                const response = await fetchConToken(`crm/${lead.id}/commercial-history`, undefined, 'GET', { signal: controller.signal });
                const body = await response.json();
                if (!response.ok || !body.ok) throw new Error(body.msg);
                if (active) setHistory(body.data);
            } catch (failure) { if (active) setError('No se pudo cargar el historial. Cierra y vuelve a abrir esta sección.'); }
        })();
        return () => { active = false; controller.abort(); };
    }, [open, lead.id, lead.crm_version]);
    const labels = { abierta: 'Abierta', ganada: 'Ganada', perdida: 'Perdida' };
    return <details className="text-xs mt-3" onToggle={event => setOpen(event.currentTarget.open)}>
        <summary className="cursor-pointer text-purple-700">Historial comercial</summary>
        {open && <div className="mt-2 space-y-2">
            {error ? <p role="alert">{error}</p> : history === null ? <p>Cargando historial…</p> : history.length === 0 ? <p>Sin cambios registrados. Los cierres antiguos pueden no tener fecha.</p> : history.map((item, index) => <div key={`${item.fecha}-${index}`} className="border-b border-gray-200 pb-2">
                <p>{labels[item.estado_anterior]} → {labels[item.estado_nuevo]} · {new Date(item.fecha).toLocaleString('es-PE', { timeZone: 'America/Lima' })}</p>
                {item.motivo && <p className="text-gray-500">{item.motivo}</p>}
            </div>)}
            {history?.length === 50 && <p>Se muestran los últimos 50 cambios.</p>}
        </div>}
    </details>;
}

export function CommercialActions({ lead, onChange, disabled }) {
    const status = lead.estado_comercial || 'abierta';
    const label = { abierta: 'Abierta', ganada: 'Ganada', perdida: 'Perdida' }[status];
    return <div className="my-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
        <p className="text-sm mb-2">Estado comercial: <strong className={status === 'ganada' ? 'text-green-700' : status === 'perdida' ? 'text-red-700' : 'text-gray-600'}>{label}</strong></p>
        {lead.fecha_cierre && <p className="text-xs text-gray-500 mb-2">Cierre: {new Date(lead.fecha_cierre).toLocaleString('es-PE', { timeZone: 'America/Lima' })}</p>}
        {status === 'perdida' && lead.motivo_perdida && <p className="text-xs text-gray-600 mb-2">Motivo: {lead.motivo_perdida}</p>}
        <div className="flex flex-wrap gap-2">
            {status === 'abierta' ? <>
                <Button size="small" color="success" variant="outlined" disabled={disabled} onClick={() => onChange(lead, 'ganada')}>Marcar como ganada</Button>
                <Button size="small" color="error" variant="outlined" disabled={disabled} onClick={() => onChange(lead, 'perdida')}>Marcar como perdida</Button>
            </> : <Button size="small" variant="outlined" disabled={disabled} onClick={() => onChange(lead, 'abierta')}>Reabrir oportunidad</Button>}
        </div>
        <CommercialHistory lead={lead} />
    </div>;
}

export function CommercialStateDialog({ state, stages, onRefresh }) {
    const [reason, setReason] = useState('');
    const [stageId, setStageId] = useState('');
    const request = state.request;
    useEffect(() => { setReason(''); setStageId(''); }, [request?.operationId]);
    if (!request) return null;
    const openStages = stages.filter(stage => !['ganado', 'perdido'].includes(stage.tipo) && stage.habilitado !== false);
    const currentOpen = openStages.some(stage => String(stage.id) === String(request.lead.id_etapa));
    const needsStage = request.status === 'abierta' && !currentOpen;
    const title = { ganada: 'Marcar oportunidad como ganada', perdida: 'Marcar oportunidad como perdida', abierta: 'Reabrir oportunidad' }[request.status];
    return <Dialog open onClose={state.close} fullWidth maxWidth="sm">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
            <p className="text-sm text-gray-600 mb-3">{request.lead.referencia || request.lead.contacto}</p>
            <p className="text-sm text-gray-500 mb-3">{request.status === 'abierta' ? 'Volverá al tablero de abiertas. El cierre anterior quedará en el historial.' : 'Se registrará la fecha de cierre y se conservará la etapa comercial.'}</p>
            {request.status === 'perdida' && <TextField autoFocus fullWidth required multiline minRows={2} label="Motivo de pérdida" value={reason} disabled={state.saving} onChange={event => setReason(event.target.value)} inputProps={{ maxLength: 1000 }} />}
            {needsStage && <label className="text-sm block">Etapa para reabrir
                <select aria-label="Etapa para reabrir" className="block w-full border rounded p-2 mt-1" value={stageId} disabled={state.saving} onChange={event => setStageId(event.target.value)}>
                    <option value="">Selecciona una etapa</option>{openStages.map(stage => <option key={stage.id} value={stage.id}>{stage.nombre}</option>)}
                </select>
            </label>}
            {state.error && <p role="alert" className="text-sm text-red-700 mt-3">{state.error.message}</p>}
        </DialogContent>
        <DialogActions>
            <Button disabled={state.saving} onClick={state.close}>Cancelar</Button>
            {state.error?.conflict ? <Button onClick={onRefresh}>Actualizar tablero</Button> :
                <Button variant="contained" color={request.status === 'perdida' ? 'error' : 'primary'} disabled={state.saving || (request.status === 'perdida' && !reason.trim()) || (needsStage && !stageId)} onClick={() => state.submit({ reason, stageId })}>{state.saving ? 'Guardando…' : 'Confirmar'}</Button>}
        </DialogActions>
    </Dialog>;
}
